import React, { useState, useEffect, useMemo, useCallback, useRef, type CSSProperties } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Table,
  Modal,
  DatePicker,
  TimePicker,
  Checkbox,
  message,
  Tooltip,
} from "antd";
import { CarOutlined, RedoOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import {
  addBookingRequest,
  addBookingReset,
  getCustomerRequest,
  getVehicleTypesRequest,
  getTripTypesRequest,
  getGoodsTypesRequest,
  getLocationsRequest,
  getAvailableCabsRequest,
  getPackagesRequest,
  getIntercityListRequest,
  addEnquiryRequest,
  addEnquiryReset,
  getMeterFareRequest,
  getNightFareRequest,
  getDistanceRequest,
  resetFareEstimate,
  getCompaniesRequest,
  clearCustomerData,
  LONG_TRIP_KM,
} from "../../redux/actions/bookingActions";
import { getNewBookingsRequest } from "../../redux/actions/newAction";
import tataAceImg from "../../assets/ace.png";
import boleroImg from "../../assets/bolero.png";
import tata407Img from "../../assets/tata407.png";
import eicherImg from "../../assets/eicher.png";
import twoWheelerImg from "../../assets/Two.png";
import threeWheelerImg from "../../assets/three.png";
import { nowIST } from "../../utils/dateUtils";
import "../../style/Add.css";
import { useBranchId } from "../../utils/hooks/useBranch";
import { useMountEffectOnce } from "../../utils/hooks/useMountEffectOnce";
import { useLocationOptions } from "../../utils/hooks/useLocationOptions";
import LocationSelect from "../../components/LocationSelect";
import type { LocationOption } from "../../utils/hooks/useLocationOptions";
import { markFormDirty, clearFormDirty } from "../../utils/branch";
import { getEmployeeName } from "../../utils/currentUser";

const { Option } = Select;

const PageGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 8px;
  /* 85px under-counted the real chrome (tab bar + card padding + motion
     wrapper) by ~70px, which is why the bottom of the form was cut off.
     The min-height floor means the grid stops shrinking on short laptops
     and the shell scrolls instead of content vanishing. */
  height: calc(100vh - 160px);
  min-height: 560px;
  padding: 8px;
  box-sizing: border-box;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr 25%;
    grid-template-rows: 1fr auto;
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    height: auto;
    overflow: auto;
  }
`;

const FormCard = styled(Card)`
  height: 95%;
  display: grid !important;
  grid-template-rows: auto 1fr auto;
  overflow: hidden;
  border-radius: 14px !important;
  background: rgb(137, 67, 154) !important;

  .ant-card-body {
    padding: 0 !important;
    display: contents;
  }
`;

const CardTitle = styled.div`
  position: relative;
  font-size: clamp(14px, 1.6vw, 20px);
  font-weight: 700;
  color: #fff;
  text-align: center;
  padding: clamp(6px, 0.8vh, 12px) 0 clamp(4px, 0.5vh, 8px);
  letter-spacing: 0.02em;
  flex-shrink: 0;
`;

/* Sits inside CardTitle's padding so the centred heading keeps its position;
   absolute placement also keeps the auto-sized header row height unchanged. */
const RefreshBtn = styled(Button)`
  position: absolute;
  right: clamp(8px, 1.1vw, 16px);
  top: 50%;
  transform: translateY(-50%);
  width: clamp(26px, 3vh, 34px) !important;
  height: clamp(26px, 3vh, 34px) !important;
  min-width: 0 !important;
  padding: 0 !important;
  border-radius: 50% !important;
  border: 1px solid rgba(255, 255, 255, 0.45) !important;
  background: rgba(255, 255, 255, 0.16) !important;
  color: #fff !important;
  font-size: clamp(11px, 1.1vw, 14px) !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.32) !important;
    color: #fff !important;
  }
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: repeat(7, 1fr);
  gap: clamp(3px, 0.55vh, 8px) clamp(6px, 0.9vw, 14px);
  padding: 0 clamp(8px, 1.1vw, 16px);
  overflow: hidden;
  min-height: 0;

  /* pairs with the PageGrid 1100px breakpoint - the two-column field
     layout has no room left once the page collapses */
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    grid-template-rows: repeat(14, minmax(44px, 1fr));
  }
`;

const FieldWrap = styled(Form.Item)`
  display: flex !important;
  flex-direction: column !important;
  margin-bottom: 0 !important;
  height: 100% !important;
  min-height: 0;
  width: 100%;

  .ant-form-item-label {
    padding-bottom: 2px !important;
    line-height: 1.2 !important;

    > label {
      font-size: clamp(9px, 0.9vw, 11px) !important;
      color: rgba(255, 255, 255, 0.85) !important;
      font-weight: 500 !important;
      height: auto !important;

      &::after {
        display: none;
      }
    }
  }

  .ant-form-item-control {
    flex: 1 !important;
    min-height: 0 !important;
    width: 100%;
  }

  .ant-form-item-control-input,
  .ant-form-item-control-input-content {
    height: 100% !important;
    width: 100%;
  }

  .ant-input,
  .ant-picker,
  .ant-select,
  .ant-select-selector {
    width: 100% !important;
    height: 100% !important;
    min-height: clamp(38px, 4.8vh, 48px) !important;

    font-size: clamp(13px, 1.2vw, 16px) !important;

    border-radius: 7px !important;
    border: 1.5px solid rgba(255, 255, 255, 0.32) !important;
    background: rgba(255, 255, 255, 0.94) !important;
  }

  .ant-select-selector {
    display: flex !important;
    align-items: center !important;
    overflow: hidden !important;
    padding-right: 28px !important;
  }

  /* antd v6 renamed the Select internals - the chosen value is plain text in
     ant-select-content and the placeholder is ant-select-placeholder, not
     ant-select-selection-placeholder - so the v5 selectors styled nothing. */
  .ant-select-content,
  .ant-select-placeholder {
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;

    font-size: clamp(13px, 1.2vw, 16px) !important;

    width: 100%;
  }

  /* BookingLayout's ConfigProvider paints Select placeholders white for the
     purple sidebar, which is invisible on these white fields. */
  .ant-select-placeholder {
    color: rgba(0, 0, 0, 0.35) !important;
  }

  .ant-picker-input > input,
  .ant-input {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* v6 draws the arrow in the suffix slot; ant-select-arrow no longer exists. */
  .ant-select-suffix {
    color: #89339c !important;
  }
`;

const BottomStrip = styled.div`
  background: #fff;
  border: 3px solid rgb(137, 67, 154);
  border-radius: 0 0 12px 12px;
  padding: clamp(5px, 0.7vh, 9px) clamp(8px, 1.1vw, 16px);
  display: flex;
  flex-direction: column;
  gap: clamp(4px, 0.5vh, 7px);
  flex-shrink: 0;
`;

const CheckRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  color: #ffffff;
  gap: clamp(8px, 1.8vw, 22px);
  row-gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  background: #916f9c9f;
  border-radius: 10px;
  padding: clamp(10px, 1.5vh, 20px) clamp(8px, 1.8vw, 22px);

  .ant-checkbox-wrapper {
    font-size: clamp(12px, 1vw, 12.5px) !important;
    font-weight: 700;
    white-space: nowrap;
  }

  .ant-checkbox-inner {
    width: clamp(12px, 1.3vw, 15px) !important;
    height: clamp(12px, 1.3vw, 15px) !important;
  }
`;

const BtnRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: clamp(8px, 1.2vw, 14px);
  padding: 0 clamp(8px, 6vw, 100px);
`;

const BookBtn = styled(Button)`
  flex: 0.5;
  height: clamp(42px, 5vh, 54px) !important;
  font-size: clamp(12px, 1.1vw, 14px) !important;
  font-weight: 700 !important;
  border-radius: 9px !important;
  border: none !important;
  background: linear-gradient(135deg, #89339c, #b56ac8) !important;
  color: #fff !important;
  &:hover { opacity: 0.88 !important; }
`;

const EnqBtn = styled(Button)`
  flex: 0.5;
  height: clamp(42px, 5vh, 54px) !important;
  font-size: clamp(12px, 1.1vw, 14px) !important;
  font-weight: 700 !important;
  border-radius: 9px !important;
  border: 2px solid #89339c !important;
  background: #f3e8f7 !important;
  color: #89339c !important;
  &:hover { background: #89339c !important; color: #fff !important; }
`;

const FareCard = styled.div`
  background: #fff;
  border-radius: 12px;
  display: grid;
  grid-template-rows: auto 1fr auto;
  overflow: hidden;
  height: 72%;
  border: 0.5px solid #e8e8e8;

  @media (max-width: 1100px) {
    grid-row: 2;
    grid-column: 2;
    height: auto;
  }

  @media (max-width: 700px) {
    height: auto;
  }
`;

const FareHeader = styled.div`
  background: rgb(137, 67, 154);
  color: #fff;
  text-align: center;
  font-weight: 700;
  font-size: clamp(12px, 1.4vw, 20px);
  padding: clamp(8px, 1.3vh, 10px) 6px;
`;

const FareBody = styled.div`
  padding: clamp(10px, 1.4vw, 20px) clamp(10px, 1.4vw, 20px);
  display: flex;
  flex-direction: column;
  gap: clamp(2px, 0.6vh, 6px);
  overflow: hidden;
  flex: 1;
  min-height: 0;
`;

const FareTypeLbl = styled.div`
  font-size: clamp(12px, 1.4vw, 20px);
  font-weight: 700;
  color: rgb(137, 67, 154);
  text-align: center;
  padding-bottom: clamp(2px, 0.3vh, 4px);
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
`;

const FareRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: clamp(11px, 1.1vw, 15px);
  padding: clamp(2px, 0.35vh, 4px) 0;
  border-bottom: 0.5px solid #f5f5f5;
  &:last-child { border-bottom: none; }
`;

const FareFooter = styled.div`
  background: rgb(137, 67, 154);
  color: #fff;
  padding: clamp(5px, 0.8vh, 10px) clamp(6px, 0.8vw, 10px);
   font-size: clamp(12px, 1.4vw, 20px);
  display: flex;
  flex-direction: column;
  gap: clamp(2px, 0.3vh, 4px);
`;

const FareFootRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: clamp(11px, 1.4vw, 20px);
  b { font-size: clamp(20px, 1.4vw, 25px); font-weight: 800; }
`;

const HistoryCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(6px, 0.8vh, 12px);
  height: 90%;
  overflow: hidden;

  @media (max-width: 1100px) {
    grid-row: 1 / 3;
    grid-column: 2;
  }

  @media (max-width: 700px) {
    height: auto;
    overflow: visible;
  }
`;

const HistoryCard = styled(Card)`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  border-radius: 12px !important;
  background: linear-gradient(145deg, rgba(137, 67, 154, 0.77), #e2e2e2) !important;
  color: #fff !important;
  box-shadow: 0 4px 16px rgba(0,0,0,0.08) !important;

  .ant-card-head {
    min-height: 0 !important;
    padding: clamp(6px, 0.8vh, 10px) clamp(10px, 1.2vw, 16px) !important;
    .ant-card-head-title {
      font-size: clamp(16px, 1.4vw, 20px) !important;
      font-weight: 700 !important;
      color: #344e41;
    }
  }

  .ant-card-body {
    padding: clamp(6px, 0.8vw, 12px) !important;
    height: calc(100% - 42px);
    overflow: hidden;
  }
`;

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    background-color: rgb(137, 67, 154) !important;
    color: #fff;
    font-size: clamp(12px, 1.1vw, 15px) !important;
    font-weight: 700;
    text-align: center;
    padding: clamp(4px, 0.5vh, 7px) 6px !important;
  }
  .ant-table-cell {
    text-align: center;
    padding: clamp(8px, 1vh, 12px) 6px !important;
    font-size: clamp(11px, 1vw, 14px) !important;
  }
  /* .ant-table-tbody > tr:hover > td {
    background-color: #f0efeb !important;
  } */
`;

const MoreButton = styled(Button)`
  margin-top: 8px;
  border-radius: 7px;
  font-size: clamp(10px, 1vw, 12px) !important;
  height: clamp(26px, 3vh, 34px) !important;
  &:hover { color: #fff !important; background: #588157 !important; border-color: #588157 !important; }
`;

const getStatusBadge = (status: number | string) => {
  const s = Number(status);
  switch (s) {
    case 1:
      return {
        text: "New booking",
        bgColor: "#f5ccb5f1",
      };
    case 2:
      return {
        text: "Live",
        bgColor: "#2e7d32",
      };
    case 3:
      return {
        text: "Complete",
        bgColor: "#eceff1"
      };
    case 5:
      return {
        text: "Cancel",
        bgColor: "#c62828",
      };
    case 6:
      return {
        text: "Enquiry",
        bgColor: "#a85bc094",
      };
    default:
      return {
        text: `Status ${status}`,
        color: "#37474f",
        bgColor: "#eceff1",
      };
  }
};

const bookingColumns = [
  {
    title: "Date",
    dataIndex: "date",
    key: "date",
    render: (d: string) => new Date(d).toLocaleDateString("en-IN"),
  },
  {
    title: "Booking ID",
    dataIndex: "tripid",
    key: "tripid",
    onCell: (record: any) => {
      const badge = getStatusBadge(record.status);
      return {
        style: {
          backgroundColor: badge.bgColor,
          color: badge.color,
          fontWeight: 700,
        },
      };
    },
    render: (text: any) => text,
  },
  {
    title: "Cab ID",
    dataIndex: "mtruckid",
    key: "mtruckid",
    render: (v: string) => v || "-",
  },
];

const availableColumns = [
  { title: "Cab ID", dataIndex: "cabid", key: "cabid" },
  { title: "Driver", dataIndex: "drivername", key: "drivername" },
  { title: "Location", dataIndex: "location", key: "location" },
];

const headerCellStyle: CSSProperties = {
  backgroundColor: "#a3b18a",
  color: "#fff",
  fontWeight: "bold",
  textAlign: "center",
};

const NEW_CUSTOMER_ID = "New";

const VEHICLE_ORDER = [
  ["ace"],
  ["bolero"],
  ["407"],
  ["eicher"],
  ["three", "3wheel"],
  ["two", "2wheel"],
];

const vehicleRank = (vehicletype: string) => {
  const name = String(vehicletype ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const rank = VEHICLE_ORDER.findIndex((names) => names.some((n) => name.includes(n)));

  return rank === -1 ? VEHICLE_ORDER.length : rank;
};

const getVehicleImage = (vehicletype: string) => {
  if (!vehicletype) return tataAceImg;

  const type = vehicletype.toLowerCase();

  if (type.includes("ace")) return tataAceImg;
  if (type.includes("bolero")) return boleroImg;
  if (type.includes("407")) return tata407Img;
  if (type.includes("eicher")) return eicherImg;
  if (type.includes("two") || type.includes("2 wheel") || type.includes("2wheel"))
    return twoWheelerImg;
  if (type.includes("three") || type.includes("3 wheel") || type.includes("3wheel"))
    return threeWheelerImg;

  return tataAceImg;
};

const convertMinutesToHrs = (minutes: number): string => {
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hrs}.${mins === 0 ? "00" : mins} Hrs`;
};

const checkIsMeterDisabled = (label: string) => {
  if (!label) return false;
  const normalized = label.toLowerCase().replace(/[^a-z0-9]/g, "");
  return normalized.includes("407") || normalized.includes("eicher14") || normalized.includes("eicher17");
};

const matchesLocationPrefix = (input: string, option?: LocationOption) =>
  String(option?.label ?? "").toLowerCase().startsWith(input.trim().toLowerCase());

const Add: React.FC = () => {
  const [form] = Form.useForm();
  const [showFullDiv, setShowFullDiv] = useState(false);
  const dispatch = useDispatch();

  const {
    success,
    addBookingError,
    customerData,
    customerNotFound,
    vehicleTypes,
    tripTypes,
    goodsTypes,
    locations,
    fareEstimate,
    packages,
    nightFare,
    enquiryLoading,
    enquirySuccess,
    enquiryError,
    distance,
    addBookingLoading,
    companies,
    intercityList,
  } = useSelector((state: any) => state.booking);

  const dynamicVehicleOptions = useMemo(
    () =>
      (vehicleTypes || [])
        .map((v: any) => ({
          value: v.id,
          label: v.vehicletype,
          img: getVehicleImage(v.vehicletype),
        }))
        .sort((a: any, b: any) => vehicleRank(a.label) - vehicleRank(b.label)),
    [vehicleTypes]
  );

  // const formattedAvailableCabs = useMemo(() => {
  //   let allCabs: any[] = [];
  //   if (Array.isArray(availableCabs)) {
  //     allCabs = availableCabs;
  //   } else if (availableCabs && typeof availableCabs === "object") {
  //     const actualData = (availableCabs as any).data || availableCabs;
  //     if (Array.isArray(actualData)) {
  //       allCabs = actualData;
  //     } else if (typeof actualData === "object") {
  //       Object.values(actualData).forEach((arr: any) => {
  //         if (Array.isArray(arr)) {
  //           allCabs = [...allCabs, ...arr];
  //         }
  //       });
  //     }
  //   }
  //   return allCabs.map((cab: any, index: number) => ({
  //     ...cab,
  //     key: cab.cabid || index.toString(),
  //     cabid: cab.cabid || "-",
  //     drivername: cab.drivername || cab.driver || "Unknown",
  //     location: cab.location || cab.place || cab.landmark || "-",
  //   }));
  // }, [availableCabs]);

  const [showFareDetails, setShowFareDetails] = useState(false);
  const selectedBranch = useBranchId();

  const lastCustomerLookup = useRef<string | null>(null);


  const DIRTY_KEY = "add-booking";
  const markDirty = useCallback(() => {
    markFormDirty(DIRTY_KEY);
  }, []);
  useEffect(() => () => clearFormDirty(DIRTY_KEY), []);
  const [selectedFareDetail, setSelectedFareDetail] = useState<any>(null);

  const [selectedVehicle, setSelectedVehicle] =
    useState<string | null>(null);

  const [isVehicleDropdownOpen, setIsVehicleDropdownOpen] =
    useState(false);

  const currentVehicleId = Form.useWatch("vehicle", form);

  const selectedVehicleObj = useMemo(() => {
    return vehicleTypes?.find((v: any) => v.id === currentVehicleId);
  }, [vehicleTypes, currentVehicleId]);

  const isMeterDisabled = useMemo(() => {
    return checkIsMeterDisabled(selectedVehicleObj?.vehicletype);
  }, [selectedVehicleObj]);

  const locationOptions = useLocationOptions();

  const handleLocationChange = () => {
    const fromLocName = form.getFieldValue("fromLocation");
    const toLocName = form.getFieldValue("toLocation");
    const vehicleId = form.getFieldValue("vehicle");
    if (fromLocName && toLocName && vehicleId) {
      const fromLoc = locations.find((l: any) => l.PlaceName === fromLocName);
      const toLoc = locations.find((l: any) => l.PlaceName === toLocName);
      if (fromLoc && toLoc) {
        const fareType = form.getFieldValue("vehicleFare");
        dispatch(getDistanceRequest(
          fromLoc.Latitude, fromLoc.Longitude,
          toLoc.Latitude, toLoc.Longitude,
          selectedBranch, vehicleId,
          fareType,
          true
        ));
      } else {
        dispatch(resetFareEstimate());
        form.setFieldsValue({ distance: "" });
      }
    }
  };

  const handleFareTypeChange = (value: any) => {
    const vehicleId = form.getFieldValue("vehicle");
    const currentDistance = form.getFieldValue("distance");

    setShowFareDetails([2, 3, 4].includes(value));

    if (!vehicleId) {
      //message.warning("Please select vehicle first");
      return;
    }
    if (value === undefined) { dispatch(resetFareEstimate()); return; }
    switch (value) {
      case 1:
        if (currentDistance) {
          dispatch(getMeterFareRequest(currentDistance, selectedBranch, vehicleId));
        } else {
          dispatch(resetFareEstimate());
        }
        break;
      case 2: dispatch(getPackagesRequest(vehicleId, selectedBranch)); break;
      case 3: dispatch(getIntercityListRequest(vehicleId, selectedBranch)); break;
      case 4: dispatch(getNightFareRequest(vehicleId, selectedBranch)); break;
    }
  };

  useEffect(() => {
    form.setFieldsValue({ datePicker: nowIST(), timePicker: nowIST() });
  }, []);

  useEffect(() => {
    if (customerData) {
      form.setFieldsValue({
        CustomerName: customerData.customerName || "",
        CompanyName: customerData.companyname || "",
        CustomerId: customerData.custmerId || NEW_CUSTOMER_ID,
      });
      //message.success("Customer loaded!");
    } else if (customerNotFound) {
      form.setFieldsValue({ CustomerId: NEW_CUSTOMER_ID });
    }
  }, [customerData, customerNotFound, form]);

  useEffect(() => {
    return () => {
      dispatch(clearCustomerData());
      dispatch(resetFareEstimate());
    };
  }, [dispatch]);

  useEffect(() => {
    if (vehicleTypes?.length > 0 && !form.getFieldValue("vehicle")) {
      const ace = vehicleTypes.find((v: any) => v.vehicletype?.toLowerCase().includes("ace"));
      if (ace) {
        form.setFieldsValue({ vehicle: ace.id });
        setSelectedVehicle(ace.id);
        // dispatch(getAvailableCabsRequest(selectedBranch, ace.id));
      }
    }
  }, [vehicleTypes]);

  useEffect(() => {
    if (tripTypes?.length > 0 && !form.getFieldValue("vehicleFare")) {
      if (isMeterDisabled) {
        const nonMeter = tripTypes.find((t: any) => !t.name?.toLowerCase().includes("meter"));
        if (nonMeter) {
          const id = Number(nonMeter.id);
          form.setFieldsValue({ vehicleFare: id });
          handleFareTypeChange(id);
        }
      } else {
        const meter = tripTypes.find((t: any) => t.name?.toLowerCase().includes("meter"));
        if (meter) {
          const id = Number(meter.id);
          form.setFieldsValue({ vehicleFare: id });
          handleFareTypeChange(id);
        }
      }
    }
  }, [tripTypes, isMeterDisabled]);

  useEffect(() => {
    if (isMeterDisabled && tripTypes?.length > 0) {
      const currentFare = form.getFieldValue("vehicleFare");
      const meter = tripTypes.find((t: any) => t.name?.toLowerCase().includes("meter"));
      const meterId = meter ? Number(meter.id) : null;
      if (currentFare === meterId) {
        form.setFieldsValue({ vehicleFare: undefined, fareDetails: undefined });
        setSelectedFareDetail(null);
        dispatch(resetFareEstimate());
        setShowFareDetails(false);
      }
    }
  }, [isMeterDisabled, tripTypes, dispatch, form]);

  useEffect(() => {
    if (distance?.distance) {
      const distVal = distance.distance.split(" ")[0];
      form.setFieldsValue({ distance: distVal });

      const vehicleId = form.getFieldValue("vehicle");
      const currentFareType = form.getFieldValue("vehicleFare");
      const numericDist = parseFloat(distVal);
      if (numericDist >= LONG_TRIP_KM && currentFareType !== 3 && vehicleId) {
        form.setFieldsValue({ vehicleFare: 3, fareDetails: undefined });
        setSelectedFareDetail(null);
        setShowFareDetails(true);
      }
    }
  }, [distance, form]);

  const resetAddBookingForm = useCallback(() => {
    form.resetFields();
    clearFormDirty(DIRTY_KEY);
    lastCustomerLookup.current = null;

    setSelectedVehicle(null);
    setSelectedFareDetail(null);
    setShowFareDetails(false);
    setIsVehicleDropdownOpen(false);
    dispatch(resetFareEstimate());
    dispatch(clearCustomerData());

    form.setFieldsValue({ datePicker: nowIST(), timePicker: nowIST() });
  }, [form, dispatch]);

  const applyDefaultVehicleAndFare = useCallback(() => {
    const ace = vehicleTypes?.find((v: any) => v.vehicletype?.toLowerCase().includes("ace"));
    if (ace) {
      form.setFieldsValue({ vehicle: ace.id });
      setSelectedVehicle(ace.id);
    }
    const meter = tripTypes?.find((t: any) => t.name?.toLowerCase().includes("meter"));
    if (meter) {
      const id = Number(meter.id);
      form.setFieldsValue({ vehicleFare: id });
      handleFareTypeChange(id);
    }
  }, [vehicleTypes, tripTypes, form]);

  const handleRefresh = useCallback(() => {
    resetAddBookingForm();
    applyDefaultVehicleAndFare();
    message.success("Form cleared");
  }, [resetAddBookingForm, applyDefaultVehicleAndFare]);

  useEffect(() => {
    if (success) {
      message.success("Booking added successfully!");
      resetAddBookingForm();
      applyDefaultVehicleAndFare();
      const ace = vehicleTypes?.find((v: any) => v.vehicletype?.toLowerCase().includes("ace"));
      if (ace) dispatch(getAvailableCabsRequest(selectedBranch, ace.id));
      dispatch(addBookingReset());
      dispatch(getNewBookingsRequest(selectedBranch));
    } else if (addBookingError) {
      message.error(addBookingError);
      dispatch(addBookingReset());
    }
  }, [success, addBookingError]);

  useEffect(() => {
    if (enquirySuccess) { message.success("Enquiry added successfully!"); resetAddBookingForm(); dispatch(addEnquiryReset()); }
    else if (enquiryError) { message.error(enquiryError); dispatch(addEnquiryReset()); }
  }, [enquirySuccess, enquiryError]);

  const currentFareType = Form.useWatch("vehicleFare", form);

  useMountEffectOnce(() => {
    if (!vehicleTypes?.length) dispatch(getVehicleTypesRequest(selectedBranch));
    if (!tripTypes?.length) dispatch(getTripTypesRequest());
    if (!goodsTypes?.length) dispatch(getGoodsTypesRequest());
    if (!locations?.length) dispatch(getLocationsRequest(selectedBranch));
    if (!companies?.length) dispatch(getCompaniesRequest(selectedBranch));
    dispatch(resetFareEstimate());
  });

  const fareTypeName =
    currentFareType === 4 ? "Night Fare" :
      currentFareType === 3 ? "Intercity Fare" :
        currentFareType === 2 ? "Package Fare" : "Meter Fare";

  const fareRows: [string, string][] =
    currentFareType === 4 && nightFare?.length > 0
      ? [
        ["Base Fare", `₹${selectedFareDetail?.basefare ?? nightFare[0]?.basefare ?? "0"}`],
        ["Base KM", `${selectedFareDetail?.basekm ?? nightFare[0]?.basekm ?? "0"} km`],
        ["Duration", `${selectedFareDetail?.baseminute ?? nightFare[0]?.baseminute ?? "0"}`],
        ["Add KM", `${selectedFareDetail?.AddKMCharge ?? nightFare[0]?.AddKMCharge ?? "0"} /km`],
        ["Add Min", `${selectedFareDetail?.AddMinuteCharge ?? nightFare[0]?.AddMinuteCharge ?? "0"} /min`],
      ]
      : currentFareType === 3 && intercityList?.length > 0
        ? [
          ["Base Fare", `₹${selectedFareDetail?.basefare ?? "0"}`],
          ["Base KM", `${selectedFareDetail?.basekm ?? "0"} km`],
          ["Base Min", `${selectedFareDetail?.baseminute ? convertMinutesToHrs(selectedFareDetail.baseminute) : "0"}`],
          ["Tolerance KM", `${selectedFareDetail?.tollerenckm ?? "0"} km`],
        ]
        : currentFareType === 2 && packages?.length > 0
          ? [
            //["Package", `${selectedFareDetail?.package ?? packages[0]?.package ?? "-"}`],
            ["Base Fare", `₹${selectedFareDetail?.basefare ?? packages[0]?.basefare ?? "0"}`],
            ["Base KM", `${selectedFareDetail?.basekm ?? packages[0]?.basekm ?? "0"} km`],
            ["Duration", `${selectedFareDetail?.baseminute ?? packages[0]?.baseminute ?? "-"}`],
          ]
          : [
            // Meter fare
            ["Base Fare", `₹${fareEstimate?.basefare ?? "0"}`],
            ["Base KM", `${fareEstimate?.basekm ?? "0"}`],
            ["Base Min", `${fareEstimate?.baseminute ?? "0"}`],
            ["Add KM", `${fareEstimate?.addkmcharge ?? "0"} /km`],
            ["Add Min", `${fareEstimate?.addminutecharge ?? "0"} /min`],
            // ["Approx KM", `${form.getFieldValue("distance") ?? "0"} km`],
            ["Approx KM", `${Math.round(Number(form.getFieldValue("distance") ?? 0))} km`],
          ];

  useEffect(() => {
    if (currentFareType === 3 && intercityList?.length > 0) {
      const dist = parseFloat(form.getFieldValue("distance"));
      if (!dist) return;

      const nearest = intercityList.reduce((prev: any, curr: any) => {
        return Math.abs(curr.basekm - dist) < Math.abs(prev.basekm - dist)
          ? curr
          : prev;
      });

      form.setFieldsValue({ fareDetails: nearest.tollerenckm });
      setSelectedFareDetail(nearest);
    }
  }, [currentFareType, intercityList, distance]);

  const buildPayload = (values: any) => {
    const fareType = Number(values.vehicleFare);
    const iscv = values.features?.includes("CV") || false;
    const istollgate = values.features?.includes("Toll Gate") || false;
    const iscredit = values.features?.includes("Prepaid") || false;
    const isgst = values.features?.includes("Intra") || false;
    const isintra = false;

    const resolvedFare =
      fareEstimate?.approximatefare ||
      (fareType === 2 ? selectedFareDetail?.basefare ?? packages[0]?.basefare :
        fareType === 3 ? selectedFareDetail?.basefare :
          fareType === 4 ? selectedFareDetail?.basefare ?? nightFare[0]?.basefare :
            "0") || "0";

    const intercityLabel = fareType === 3 && selectedFareDetail
      ? `${selectedFareDetail.basekm}-${selectedFareDetail.basefare} - ${convertMinutesToHrs(selectedFareDetail.baseminute)}`
      : values.fareDetails;

    const packagetype = fareType === 2 ? values.fareDetails : undefined;
    const intercitytype = fareType === 3 ? intercityLabel : undefined;
    const nighttype = fareType === 4 ? values.fareDetails : undefined;

    const companyname = String(values.company || values.CompanyName || "").trim();

    return {
      name: values.CustomerName,
      mobileno: values.MobileNumber,
      fromloc: values.fromLocation,
      toloc: values.toLocation,
      tripdate: values.datePicker ? values.datePicker.format("YYYY-MM-DD") : "",
      triptime: values.timePicker ? values.timePicker.format("HH:mm:ss") : "",
      vechicletype: Number(values.vehicle),
      triptype: String(values.vehicleFare),
      goodstype: values.googleType,
      alternativemobno: values.alternateNumber,
      distance: values.distance,
      branchid: selectedBranch,
      fare: resolvedFare,
      username: getEmployeeName(),
      employeename: getEmployeeName(),
      remark: values.remarks || "",
      refferedby: values.referedBy,
      iscv, istollgate, isgst, iscredit, isintra,
      ...(companyname ? { companyname } : {}),
      noofvechicles: String(values.noOfVehicles ?? 1),
      isadditionalfare: false,
      packagetype,
      intercitytype,
      nighttype
    };
  };

  const onFinish = (values: any) => {
    const now = nowIST();
    const selDate = values.datePicker;
    const selTime = values.timePicker;
    const isToday = selDate?.isSame(now, "day");

    let finalTime = selTime;
    if (isToday && selTime.isBefore(now, "minute")) {
      finalTime = now;
      form.setFieldsValue({ timePicker: now });
    }

    dispatch(addBookingRequest({
      ...buildPayload({
        ...values,
        timePicker: finalTime,
      }),
    }));
  };

  const onEnquiry = async () => {
    try {
      const values = await form.validateFields();
      dispatch(addEnquiryRequest(buildPayload(values)));
    } catch {
      message.error("Please fill all required fields before making an enquiry.");
    }
  };
  const selectStyle: React.CSSProperties = { width: "100%", height: "100%" };
  const inputStyle: React.CSSProperties = { width: "100%", height: "100%" };

  return (
    <>
      <PageGrid className="tt-add-page">

        <FormCard>
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            onValuesChange={markDirty}
            initialValues={{ datePicker: nowIST(), timePicker: nowIST(), noOfVehicles: 1 }}
            style={{ display: "contents" }}
          >
            <CardTitle>
              Add Booking
              <Tooltip title="Clear form">
                <RefreshBtn
                  type="text"
                  aria-label="Clear form"
                  icon={<RedoOutlined />}
                  onClick={handleRefresh}
                />
              </Tooltip>
            </CardTitle>

            <FieldsGrid>

              <FieldWrap
                name="CustomerName"
                rules={[{ required: true, message: "Required!" }]}
                getValueFromEvent={(e) => {
                  return e.target.value
                    .replace(/[^a-zA-Z\s]/g, "")
                    .replace(/\s+/g, " ")
                    .trimStart();
                }}
              >
                <Input placeholder="Customer Name" autoComplete="off" style={inputStyle} />
              </FieldWrap>

              <FieldWrap name="MobileNumber"
                rules={[
                  { required: true, message: "Required!" },
                  { pattern: /^[0-9]{10}$/, message: "10 digits only" },
                ]}>
                <Input
                  placeholder="Mobile Number"
                  autoComplete="off"
                  maxLength={10}
                  style={inputStyle}
                  onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }}
                  onChange={(e) => {
                    const mobile = e.target.value;
                    if (!/^[0-9]{10}$/.test(mobile)) {
                      lastCustomerLookup.current = null;
                      return;
                    }
                    if (lastCustomerLookup.current === mobile) return;
                    lastCustomerLookup.current = mobile;
                    dispatch(getCustomerRequest(mobile, selectedBranch));
                  }}
                />
              </FieldWrap>

              <FieldWrap name="CompanyName"
              //rules={[{ required: true, message: "Required!" }]}
              >
                <Select
                  showSearch allowClear placeholder="Select Company" style={selectStyle}
                  optionFilterProp="label"
                  filterOption={(i, o) =>
                    String(o?.label ?? "").toLowerCase().includes(i.toLowerCase())}
                  options={companies?.map((c: any) => ({
                    label: c.companyname, value: c.companyname,
                  }))}
                />
              </FieldWrap>

              <FieldWrap name="CustomerId"
                rules={[{ required: true, message: "Required!" }]}>
                <Input placeholder="Customer ID" style={inputStyle} />
              </FieldWrap>

              <FieldWrap name="fromLocation"
                rules={[{ required: true, message: "Required!" }]}>
                <LocationSelect
                  placeholder="From Location"
                  options={locationOptions}
                  filterOption={matchesLocationPrefix}
                  onPicked={() => {
                    form.setFieldsValue({ fareDetails: undefined });
                    setSelectedFareDetail(null);
                    handleLocationChange();
                  }}
                />
              </FieldWrap>

              <FieldWrap name="toLocation"
                rules={[{ required: true, message: "Required!" }]}>
                <LocationSelect
                  placeholder="To Location"
                  options={locationOptions}
                  filterOption={matchesLocationPrefix}
                  onPicked={() => handleLocationChange()}
                />
              </FieldWrap>

              <FieldWrap name="datePicker"
                rules={[{ required: true, message: "Required!" }]}>
                <DatePicker
                  placeholder="Select Date"
                  style={selectStyle}
                  format={"DD/MM/YYYY"}
                  disabledDate={(current) => current && current.isBefore(nowIST(), "day")}
                />
              </FieldWrap>

              <FieldWrap name="timePicker"
                rules={[{ required: true, message: "Required!" }]}>
                <TimePicker
                  placeholder="Select Time"
                  format="hh:mm A"
                  use12Hours
                  style={selectStyle}
                  onChange={(time) => {
                    if (!time) return;

                    const selectedDate = form.getFieldValue("datePicker");
                    const isToday = selectedDate?.isSame(nowIST(), "day");

                    if (isToday && time.isBefore(nowIST(), "minute")) {
                      form.setFieldsValue({ timePicker: nowIST() });
                    }
                  }}
                />
              </FieldWrap>

              <FieldWrap name="googleType"
                rules={[{ required: true, message: "Required!" }]}>
                <Select
                  showSearch placeholder="Select Goods Type"
                  style={selectStyle} optionFilterProp="children"
                >
                  {goodsTypes?.map((g: any) => (
                    <Option key={g.goodsid} value={g.goodsname}>{g.goodsname}</Option>
                  ))}
                </Select>
              </FieldWrap>
              <FieldWrap>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    height: "100%",
                    width: "100%",
                    alignItems: "stretch",
                  }}
                >
                  <Form.Item
                    name="vehicle"
                    noStyle
                    rules={[{ required: true, message: "Required!" }]}
                  >
                    <Select
                      placeholder="Select Vehicle"
                      style={{ flex: 1, minWidth: 0, width: "100%", height: "100%" }}
                      value={selectedVehicle ?? undefined}
                      open={isVehicleDropdownOpen}
                      onOpenChange={(val) =>
                        setIsVehicleDropdownOpen(val)
                      }
                      onChange={(val) => setSelectedVehicle(val)}
                      styles={{
                        popup: {
                          root: {
                            minWidth: "min(420px, 90vw)",
                            maxWidth: "min(560px, 90vw)",
                            borderRadius: "12px",
                            padding: "6px",
                          },
                        },
                      }}
                      popupRender={() => (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "nowrap",
                            gap: "12px",
                            padding: "12px",
                            overflowX: "auto",
                          }}
                        >
                          {dynamicVehicleOptions.map((v: any) => {
                            const isSelected = selectedVehicle === v.value;

                            return (
                              <div
                                key={v.value}
                                onMouseDown={(e) => {
                                  e.preventDefault();

                                  setSelectedVehicle(v.value);
                                  form.setFieldValue("vehicle", v.value);
                                  setIsVehicleDropdownOpen(false);

                                  form.setFieldsValue({ fareDetails: undefined });
                                  setSelectedFareDetail(null);
                                  // dispatch(resetFareEstimate());
                                  // dispatch(clearFareLists());
                                  // dispatch(
                                  //   getAvailableCabsRequest(
                                  //     selectedBranch,
                                  //     v.value
                                  //   )
                                  // );
                                  //handleLocationChange();

                                  const currentFareType = form.getFieldValue("vehicleFare");
                                  const isNewMeterDisabled = checkIsMeterDisabled(v.label);
                                  if (currentFareType) {
                                    const currentDistance = form.getFieldValue("distance");
                                    if (isNewMeterDisabled && currentFareType === 1) {
                                      form.setFieldsValue({ vehicleFare: undefined, fareDetails: undefined });
                                      setSelectedFareDetail(null);
                                      dispatch(resetFareEstimate());
                                      setShowFareDetails(false);
                                    } else {
                                      setShowFareDetails([2, 3, 4].includes(currentFareType));
                                      switch (currentFareType) {
                                        case 1:
                                          if (currentDistance)
                                            dispatch(getMeterFareRequest(currentDistance, selectedBranch, v.value));
                                          else
                                            handleLocationChange();
                                          break;
                                        case 2:
                                          dispatch(getPackagesRequest(v.value, selectedBranch));
                                          break;
                                        case 3:
                                          dispatch(getIntercityListRequest(v.value, selectedBranch));
                                          break;
                                        case 4:
                                          dispatch(getNightFareRequest(v.value, selectedBranch));
                                          break;
                                      }
                                    }
                                  }
                                }}
                                style={{
                                  cursor: "pointer",
                                  borderRadius: "12px",
                                  border: isSelected
                                    ? "2.5px solid #89339C"
                                    : "2px solid #e0e0e0",

                                  background: isSelected
                                    ? "linear-gradient(135deg,#f3e8f7,#e8d0f5)"
                                    : "#fafafa",

                                  boxShadow: isSelected
                                    ? "0 4px 12px rgba(137,67,154,0.3)"
                                    : "0 2px 6px rgba(0,0,0,0.07)",

                                  padding: "10px",
                                  textAlign: "center",
                                  transition: "all 0.2s ease",

                                  transform: isSelected
                                    ? "scale(1.05)"
                                    : "scale(1)",

                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  gap: "6px",

                                  minWidth: "110px",
                                }}
                              >
                                <img
                                  src={v.img}
                                  alt={v.label}
                                  style={{
                                    width: "75px",
                                    height: "52px",
                                    objectFit: "contain",
                                    filter: isSelected
                                      ? "none"
                                      : "grayscale(10%)",
                                  }}
                                />

                                <div
                                  style={{
                                    fontWeight: 600,
                                    fontSize: "12px",
                                    color: isSelected
                                      ? "#89339C"
                                      : "#444",
                                  }}
                                >
                                  {v.label}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    >
                      {dynamicVehicleOptions.map((v: any) => (
                        <Option key={v.value} value={v.value}>
                          {v.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Tooltip title="No of vehicles">
                    <div
                      style={{
                        flex: "0 0 clamp(60px, 6.5vw, 84px)",
                        minWidth: 0,
                        height: "100%",
                      }}
                    >
                      <Form.Item name="noOfVehicles" noStyle>
                        <Select
                          aria-label="No of vehicles"
                          style={{ width: "100%", height: "100%" }}
                        >
                          {[1, 2, 3, 4, 5].map((n) => (
                            <Option key={n} value={n}>
                              {n}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>
                  </Tooltip>
                </div>
              </FieldWrap>

              <FieldWrap>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    height: "100%",
                    width: "100%",
                    alignItems: "stretch",
                  }}
                >
                  <Form.Item
                    name="vehicleFare"
                    noStyle
                    rules={[{ required: true, message: "Required!" }]}
                  >
                    <Select
                      placeholder="Select Fare Type"
                      style={{
                        flex: 1,
                        minWidth: 0,
                        width: "100%",
                        height: "100%",
                      }}
                      onChange={(value) => {
                        form.setFieldsValue({ fareDetails: undefined });
                        handleFareTypeChange(value);
                      }}
                      allowClear
                    >
                      {tripTypes
                        ?.filter((t: any) => {
                          if (isMeterDisabled && t.name?.toLowerCase().includes("meter")) {
                            return false;
                          }
                          return true;
                        })
                        ?.map((t: any) => (
                          <Option key={t.id} value={Number(t.id)}>
                            {t.name}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>

                  {showFareDetails && (
                    <Form.Item
                      name="fareDetails"
                      noStyle
                      rules={[{ required: true, message: "Required!" }]}
                    >
                      {[2, 3, 4].includes(currentFareType) ? (

                        <Select
                          placeholder="Select Details"
                          style={{
                            flex: 1,
                            minWidth: 0,
                            width: "100%",
                            height: "100%",
                          }}
                          onChange={(value) => {
                            const fareType = form.getFieldValue("vehicleFare");
                            let selectedItem = null;

                            if (fareType === 2) {
                              selectedItem = packages?.find((pkg: any) => pkg.package === value);
                            } else if (fareType === 3) {
                              selectedItem = intercityList?.find((ic: any) => ic.tollerenckm === value);
                            } else if (fareType === 4) {
                              selectedItem = nightFare?.find((nf: any) => nf.package === value);
                            }
                            //console.log("Selected fare detail:", selectedItem);
                            setSelectedFareDetail(selectedItem);
                          }}
                        >
                          {currentFareType === 3 ? (
                            intercityList?.map((ic: any) => (
                              <Option key={ic.tollerenckm} value={ic.tollerenckm} basekm={ic.basekm} basefare={ic.basefare} baseminute={ic.baseminute}>
                                {`${ic.basekm}-${ic.basefare} - ${convertMinutesToHrs(ic.baseminute)}`}
                              </Option>
                            ))
                          ) : (
                            [...(form.getFieldValue("vehicleFare") === 2 ? packages : nightFare) ?? []]
                              .sort((a: any, b: any) => Number(a.basekm) - Number(b.basekm))
                              .map((pkg: any) => (
                                <Option key={pkg.id} value={pkg.package}>
                                  {pkg.package}
                                </Option>
                              ))
                          )}
                        </Select>
                      ) : (
                        <Input
                          placeholder="Fare Details"
                          style={{ flex: 1, height: "100%", minWidth: 0 }}
                        />
                      )}
                    </Form.Item>
                  )}
                </div>
              </FieldWrap>

              <FieldWrap name="alternateNumber">
                <Input placeholder="Alternate Number" autoComplete="off" maxLength={10} style={inputStyle}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "");
                    form.setFieldsValue({ alternateNumber: v });
                  }}
                />
              </FieldWrap>

              <FieldWrap name="referedBy">
                <Input placeholder="Referred By" autoComplete="off" style={inputStyle} />
              </FieldWrap>

              <FieldWrap name="remarks">
                <Input placeholder="Remarks" autoComplete="off" style={inputStyle} />
              </FieldWrap>

            </FieldsGrid>

            <BottomStrip>
              <CheckRow>
                <Form.Item name="features" noStyle>
                  <Checkbox.Group style={{
                    display: "flex", justifyContent: "space-between",
                    gap: "clamp(8px,1.8vw,22px)", width: "100%",
                  }}>
                    {["CV", "Prepaid", "Toll Gate", "Intra"].map((opt) => (
                      <Checkbox key={opt} value={opt}
                        style={{ fontSize: "clamp(10px,1vw,12.5px)", fontWeight: 700 }}>
                        {opt}
                      </Checkbox>
                    ))}
                  </Checkbox.Group>
                </Form.Item>
              </CheckRow>

              <BtnRow>
                <BookBtn htmlType="submit" loading={addBookingLoading}>🚕 Book Ride</BookBtn>
                <EnqBtn htmlType="button" onClick={onEnquiry} loading={enquiryLoading}>💬 Make Enquiry</EnqBtn>
              </BtnRow>
            </BottomStrip>

          </Form>
        </FormCard>

        <FareCard>
          <FareHeader>Fare Estimation</FareHeader>
          <FareBody>
            <FareTypeLbl>{fareTypeName}</FareTypeLbl>
            {fareRows.map(([label, value], i) => (
              <FareRow key={i}>
                <span style={{ color: "#777", fontWeight: 500 }}>{label}</span>
                <span style={{ fontWeight: 700, color: "#333" }}>{value}</span>
              </FareRow>
            ))}
          </FareBody>
          <FareFooter>
            <FareFootRow>
              <span>Approx Fare</span>
              <b>{fareEstimate?.approximatefare ||
                (currentFareType === 2 ? selectedFareDetail?.basefare ?? packages[0]?.basefare :
                  currentFareType === 3 ? selectedFareDetail?.basefare :
                    currentFareType === 4 ? selectedFareDetail?.basefare ?? nightFare[0]?.basefare :
                      "0") || "0"}</b>
            </FareFootRow>
            {/* <FareFootRow>
              <span>ETA</span>
              <b>00</b>
            </FareFootRow> */}
          </FareFooter>
        </FareCard>

        <HistoryCol>
          <HistoryCard title="📖 Booking History" style={{ flex: 1, minHeight: 0 }}>
            <StyledTable
              rowKey="tripid"
              columns={bookingColumns.map((c) => ({
                ...c,
                onHeaderCell: () => ({ style: headerCellStyle }),
              }))}
              dataSource={customerData?.triphistory || []}
              bordered
              pagination={{
                pageSize: 10,
                pageSizeOptions: ["5", "10", "100"],
                showSizeChanger: true,
              }}
              scroll={{ x: "max-content", y: "calc(100vh - 500px)" }}
            />
          </HistoryCard>

          <HistoryCard title="💳 Available Cab" style={{ flex: 1, minHeight: 0 }}>
            <StyledTable
              rowKey="cabid"
              columns={availableColumns.map((c) => ({
                ...c,
                onHeaderCell: () => ({ style: headerCellStyle }),
              }))}
              //dataSource={formattedAvailableCabs}
              bordered
              pagination={{
                pageSize: 10,
                pageSizeOptions: ["5", "10", "100"],
                showSizeChanger: true,
              }}
              scroll={{ x: "max-content", y: "calc(100vh - 500px)" }}
            />
            <MoreButton type="primary" ghost onClick={() => setShowFullDiv(true)}>
              Show More
            </MoreButton>
          </HistoryCard>
        </HistoryCol>

      </PageGrid>

      <Modal
        title={
          <div style={{ fontSize: 22, fontWeight: 700, color: "#89339C", textAlign: "center" }}>
            <CarOutlined style={{ fontSize: 26, marginRight: 10 }} />
            Available Cab
          </div>
        }
        open={showFullDiv}
        onCancel={() => setShowFullDiv(false)}
        footer={null}
        width={{ xs: "94vw", md: 800 }}
      >
        <Table
          rowKey="cabid"
          columns={availableColumns.map((c) => ({
            ...c,
            align: "center" as const,
            onHeaderCell: () => ({
              style: {
                backgroundColor: "rgb(137, 67, 154)",
                color: "#fff",
                fontWeight: "bold",
                textAlign: "center",
              },
            }),
          }))}
          //dataSource={formattedAvailableCabs}
          bordered
        />
      </Modal>
    </>
  );
};

export default Add;
